import { Stack, useTheme } from "@mui/material";
import { FinancesStepLayout } from "web_myplann/Finances/Components/FinancesStepLayout";
import { IncomeFormSheet } from "web_myplann/Finances/Components/IncomeFormSheet";
import { IncomeRowList } from "web_myplann/Finances/Components/IncomeRowList";
import { LeaveFinancesDrawer } from "web_myplann/Finances/Components/LeaveFinancesDrawer";
import { useIncomeV2Form } from "web_myplann/Finances/Forms/useIncomeV2Form";
import { getIncomeBackRoute } from "web_myplann/Finances/Helpers/getIncomeBackRoute";
import { useFinanceData } from "web_myplann/Finances/Hooks/useFinanceData";
import { useFinancesWizardNavigation } from "web_myplann/Finances/Hooks/useFinancesWizardNavigation";
import { useIncomeEditor } from "web_myplann/Finances/Hooks/useIncomeEditor";
import { useLeaveFinancesGuard } from "web_myplann/Finances/Hooks/useLeaveFinancesGuard";
import { IncomeBackTo } from "web_myplann/Finances/Types/FinancesNavigationState";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { AddRowButton } from "web_myplann/Shared/Components/AddRowButton";
import { SectionHeader } from "web_myplann/Shared/Components/SectionHeader";

const R = Resources.Finances;

export const IncomeV2Page = () => {
	const { t } = useResource();
	const theme = useTheme();
	const { navigationState, goToStep } = useFinancesWizardNavigation();
	const backTo = navigationState.incomeBackTo ?? IncomeBackTo.Intro;

	const { isLoading } = useFinanceData({
		hydrateDraft: !navigationState.keepDraft,
	});

	const {
		incomes,
		incomeKeys,
		incomeAppend,
		removeIncome,
		setValue,
		onSubmit,
		exact,
		total,
		setExact,
		setManualTotal,
		hasUnconfirmedChanges,
	} = useIncomeV2Form();

	const editor = useIncomeEditor({ incomes, incomeAppend, setValue });
	const { isLeaving, stay, leave } = useLeaveFinancesGuard(
		hasUnconfirmedChanges,
	);

	const handleContinue = async () => {
		await onSubmit();
		goToStep(Routing.Finances.Expenses.path(), {
			...navigationState,
			keepDraft: true,
		});
	};

	const handleBackToOverview = async () => {
		await onSubmit();
		goToStep(Routing.Finances.Overview.path());
	};

	if (isLoading) {
		return null;
	}

	return (
		<>
			<FinancesStepLayout
				backRoute={getIncomeBackRoute(backTo)}
				alwaysUseDefaultRoute={backTo !== IncomeBackTo.Dashboard}
				backState={navigationState}
				onBack={
					backTo === IncomeBackTo.Overview ? handleBackToOverview : undefined
				}
				description={t(R.Income.descriptionTentative)}
				tentativeLabel={t(R.Income.titleTentatively)}
				exactLabel={t(R.Income.titleExact)}
				exact={exact}
				hasItems={incomes.length > 0}
				onExactChange={setExact}
				total={total}
				onTotalChange={setManualTotal}
				whatToCount="income"
				continueLabel={t(R.Income.continue)}
				onContinue={handleContinue}
			>
				<SectionHeader title={t(R.Income.sourcesTitle)} />
				<Stack bgcolor={theme.palette.background.paper}>
					<IncomeRowList
						items={incomes}
						rowKeys={incomeKeys}
						onEdit={editor.openEdit}
						onRemove={removeIncome}
					/>
					<AddRowButton
						label={t(R.Income.addIncome)}
						onClick={editor.openAdd}
						noBorder={incomes.length > 0}
					/>
				</Stack>
			</FinancesStepLayout>

			<IncomeFormSheet
				open={editor.open}
				isEditing={editor.isEditing}
				initialValues={editor.initialValues}
				onClose={editor.close}
				onSubmit={editor.submit}
			/>
			<LeaveFinancesDrawer open={isLeaving} onStay={stay} onLeave={leave} />
		</>
	);
};
